import { prefersReducedMotion } from '@theme/utilities';

const REVEAL_SELECTOR = [
  '.shopify-section .section-content-wrapper > *',
  '.shopify-section .group-block',
  '.shopify-section .resource-list__item',
  '.shopify-section .product-card',
  '.shopify-section .collection-card',
  '.shopify-section .resource-card',
  '.shopify-section h1, .shopify-section h2, .shopify-section h3, .shopify-section h4, .shopify-section h5, .shopify-section h6',
  '.shopify-section p',
  '.shopify-section .button',
].join(', ');

/** @type {IntersectionObserver | null} */
let observer = null;

function shouldSkip(element) {
  if (!(element instanceof HTMLElement)) return true;
  if (element.closest('header, #header-group')) return true;
  if (element.closest('dialog, dialog-component, .menu-drawer, .cart-drawer')) return true;
  if (!element.offsetParent && getComputedStyle(element).position !== 'fixed') return true;
  return false;
}

function isNearTopOfPage(element) {
  const viewportHeight = window.innerHeight || 0;
  if (!viewportHeight) return true;
  const rect = element.getBoundingClientRect();
  // Skip only elements currently visible in the initial viewport.
  return rect.top < viewportHeight && rect.bottom > 0;
}

function reveal(element) {
  element.classList.add('is-scroll-reveal-visible');
}

function observe(element) {
  if (!observer) return;
  element.setAttribute('data-scroll-reveal', '');
  observer.observe(element);
}

function initProductMediaLoadReveal(root = document) {
  const main = document.querySelector("main[data-template*='product']");
  if (!main) return;

  const mediaImages = root.querySelectorAll('.product-information .product-media__image');
  for (const image of mediaImages) {
    if (!(image instanceof HTMLImageElement)) continue;
    if (image.dataset.mediaLoadRevealBound === 'true') continue;
    image.dataset.mediaLoadRevealBound = 'true';
    image.setAttribute('data-media-load-reveal', '');

    const show = () => {
      image.classList.add('is-media-load-visible');
    };

    if (image.complete && image.naturalWidth > 0) {
      show();
      continue;
    }

    image.addEventListener('load', show, { once: true });
    image.addEventListener('error', show, { once: true });
  }
}

function initScrollReveal(root = document) {
  if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;

  if (!observer) {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const element = /** @type {HTMLElement} */ (entry.target);
          reveal(element);
          observer?.unobserve(element);
        }
      },
      { root: null, threshold: 0.12, rootMargin: '0px 0px -8% 0px' },
    );
  }

  const elements = root.querySelectorAll(REVEAL_SELECTOR);
  for (const element of elements) {
    if (shouldSkip(element)) continue;
    if (element.closest('[data-scroll-reveal]')) continue;
    if (isNearTopOfPage(element)) continue;
    if (element.classList.contains('is-scroll-reveal-visible')) continue;
    observe(element);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initScrollReveal();
    initProductMediaLoadReveal();
  });
} else {
  initScrollReveal();
  initProductMediaLoadReveal();
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  initScrollReveal(event.target);
  initProductMediaLoadReveal(event.target);
});
